<template>
  <b-overlay :show="loading" opacity="0.5">
    <add-or-update-patient-modal
      :is-add-active.sync="isAddOrUpdatePatientToggle"
      @refetchData="fetchAllData"
    />

    <div>
      <list-table
        ref="users_table_ref"
        :records="patients" 
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        search-place-holder="Search Name"
        :current-page-number.sync="currentPage"
        :show-export-button="can('users:CanExport')"
        :show-import-button="false"
        @row-clicked="onRowClicked"
        @export="onExportPatients"
      >
        <template v-if="can('patients:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="isAddOrUpdatePatientToggle = true"
          >
            <span class="align-middle text-lg">Add new patient</span>
          </b-button>
        </template>

        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select select-size-md"
                placeholder="Gender Status"
                :disabled="loading"
                :value="genderFilter"
                :options="genderOptions"
                :reduce="val => val.value"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                @input="(val) => genderFilter = val"
              />
            </b-col>
            
            <b-col cols="12" md="2" class="">
              <b-form-input
                v-model="phoneNumberFilter"
                placeholder="Phone number"
              />
            </b-col>

            <b-col cols="12" md="3" class="">
              <b-form-input
                v-model="emailAddressFilter"
                placeholder="Email address"
              />
            </b-col>
          </b-row>

        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="data.item.status === 'active' ? `light-success` : 'light-danger'"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            
            <b-dropdown-item :to="{ name: 'admin-update-patient', params: { id: data.item._id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item :to="{ name: 'admin-patient-details', params: { id: data.item._id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce, startCase } from "lodash"

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddOrUpdatePatientModal from '@/@core/components/shared/admin/modals/AddOrUpdatePatientModal.vue';

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}


export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 

    vSelect,
    AddOrUpdatePatientModal
  },
  directives: {
    Ripple,
  },
  props: {
    showAddUserButton: {
      type: Boolean,
      default: true
    },
    clientGroupId: {
      type: String,
      default: ""
    }
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      showListTable: false,
      isAddOrUpdatePatientToggle: false,

      searchFilter: null,
      genderFilter: null,
      phoneNumberFilter: null,
      emailAddressFilter: null,

      patients: [],

      tableColumns: [
        { key: 'first_name', sortable: false, label: 'First Name' },
        { key: 'last_name', sortable: false, label: 'Last Name' },
        { key: 'email', sortable: false },
        { key: 'phone', sortable: false },
        { key: 'policy_number', sortable: false },
        { key: 'partner', sortable: false },
        { key: 'gender', sortable: false, formatter: startCase },
        { key: 'date_of_birth', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'age', sortable: false, },
        { key: 'status' },
        { key: 'created', label: 'Joined on', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      genderOptions: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    genderFilter: watchHandlerAndResetPageNumber,
    searchFilter: watchHandlerAndResetPageNumber,
    phoneNumberFilter: watchHandlerAndResetPageNumber,
    emailAddressFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()

    const { 
      page = 1, 
      name = "", 
      phone = "", 
      gender = "", 
      email = "", 
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = name;
    this.genderFilter = gender;
    this.phoneNumberFilter = phone;
    this.emailAddressFilter = email;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          name: this.searchFilter,
          gender: this.genderFilter,
          phone: this.phoneNumberFilter,
          email: this.emailAddressFilter,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.patients.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.patients = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async onExportPatients(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          name: this.searchFilter,
          gender: this.genderFilter,
          phone: this.phoneNumberFilter,
          policy_status: this.policyStatusFilter,
          policy_number: this.emailAddressFilter,
        }

        const response = await this.useJwt().mainService.patients.export(query);
        FileDownload(response.data, "patients-export.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onRowClicked(rowItem){
      this.$router.push({ name: 'admin-patient-details', params: { id: rowItem._id } })
      .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
